import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Separator } from "@/components/ui/separator";
import { Plus, ArrowDown, ArrowUp, CalendarIcon, MoreHorizontal, Check, ChevronsUpDown } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { useWOAdjustments, useCreateAdjustment, useTransitionAdjustment, computeAdjustmentTotals, type WOAdjustment } from "@/hooks/useWOAdjustments";
import { useVendors } from "@/hooks/useVendors";
import { useContractSovLines, useProjectContracts } from "@/hooks/useProjectContracts";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { format, addBusinessDays } from "date-fns";
import { cn } from "@/lib/utils";

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);

function VendorCombobox({
  value, onChange, vendors, placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  vendors: { id: string; name: string }[];
  placeholder: string;
}) {
  const [open, setOpen] = useState(false);
  const selected = vendors.find(v => v.id === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" className="w-full justify-between font-normal">
          <span className={cn(!selected && "text-muted-foreground")}>
            {selected ? selected.name : placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search vendor…" />
          <CommandList onWheel={(e) => e.stopPropagation()}>
            <CommandEmpty>No vendor found.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="__none__"
                onSelect={() => { onChange(""); setOpen(false); }}
              >
                <Check className={cn("mr-2 h-4 w-4", !value ? "opacity-100" : "opacity-0")} />
                None
              </CommandItem>
              {vendors.map(v => (
                <CommandItem
                  key={v.id}
                  value={v.name}
                  onSelect={() => { onChange(v.id); setOpen(false); }}
                >
                  <Check className={cn("mr-2 h-4 w-4", value === v.id ? "opacity-100" : "opacity-0")} />
                  {v.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}



const ADJUSTMENT_TYPES = [
  { value: "backcharge", label: "Backcharge", defaultDir: "debit" },
  { value: "rework", label: "Rework", defaultDir: "debit" },
  { value: "damage", label: "Damage", defaultDir: "debit" },
  { value: "warranty", label: "Warranty Recall", defaultDir: "debit" },
  { value: "material_return", label: "Material Return", defaultDir: "credit" },
  { value: "bonus", label: "Bonus", defaultDir: "credit" },
  { value: "owner_change", label: "Owner Change", defaultDir: "credit" },
  { value: "dispute", label: "Dispute", defaultDir: "debit" },
  { value: "other", label: "Other", defaultDir: "debit" },
];

const TYPE_COLORS: Record<string, string> = {
  backcharge: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
  rework: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
  damage: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
  warranty: "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200",
  dispute: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
  bonus: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  material_return: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  owner_change: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
  other: "bg-muted text-muted-foreground",
};

const STATUS_COLORS: Record<string, string> = {
  draft: "bg-muted text-muted-foreground",
  pending_approval: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
  approved: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  disputed: "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200",
  applied: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
  voided: "bg-muted text-muted-foreground line-through",
};

interface Props {
  workOrderId: string;
  projectId: string;
  onMutate: () => void;
}

export const WOAdjustmentsTab = ({ workOrderId, projectId, onMutate }: Props) => {
  const { data: adjustments } = useWOAdjustments(workOrderId);
  const createAdj = useCreateAdjustment();
  const transitionAdj = useTransitionAdjustment();
  const { data: vendors } = useVendors();
  const { data: contracts } = useProjectContracts(projectId);
  const contract = contracts?.[0];
  const { data: sovLines } = useContractSovLines(contract?.id);

  const [modalOpen, setModalOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [disputeModal, setDisputeModal] = useState<string | null>(null);
  const [disputeReason, setDisputeReason] = useState("");

  // Form state
  const [adjType, setAdjType] = useState("backcharge");
  const [direction, setDirection] = useState("debit");
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [sovLineId, setSovLineId] = useState("");
  const [responsiblePartyId, setResponsiblePartyId] = useState("");
  const [causedById, setCausedById] = useState("");
  const [disputeDeadline, setDisputeDeadline] = useState<Date | undefined>();
  const [notes, setNotes] = useState("");

  const totals = computeAdjustmentTotals(adjustments ?? []);

  const openModal = () => {
    setAdjType("backcharge");
    setDirection("debit");
    setAmount("");
    setDescription("");
    setSovLineId("");
    setResponsiblePartyId("");
    setCausedById("");
    setDisputeDeadline(addBusinessDays(new Date(), 5));
    setNotes("");
    setModalOpen(true);
  };

  const handleTypeChange = (type: string) => {
    setAdjType(type);
    const def = ADJUSTMENT_TYPES.find(t => t.value === type);
    if (def) setDirection(def.defaultDir);
    if (type === "backcharge") {
      setDisputeDeadline(addBusinessDays(new Date(), 5));
    }
  };

  const handleCreate = async (submitForApproval: boolean) => {
    if (!description.trim()) return toast.error("Description required");
    if (!amount || Number(amount) <= 0) return toast.error("Amount must be > 0");

    setBusy(true);
    const { data: companyId } = await supabase.rpc("my_company_id");
    if (!companyId) { setBusy(false); return toast.error("Company not found"); }

    try {
      const adj = await createAdj.mutateAsync({
        company_id: companyId,
        work_order_id: workOrderId,
        adjustment_type: adjType,
        direction,
        amount: Number(amount),
        description: description.trim(),
        sov_line_id: sovLineId || null,
        responsible_party_id: responsiblePartyId || null,
        caused_by_vendor_id: causedById || null,
        dispute_deadline: adjType === "backcharge" && disputeDeadline
          ? disputeDeadline.toISOString()
          : null,
        notes: notes || null,
        status: submitForApproval ? "pending_approval" : "draft",
      } as any);

      if (submitForApproval && adj) {
        // Log to audit
        await supabase.from("ops_audit_log" as any).insert({
          company_id: companyId,
          table_name: "wo_adjustments",
          record_id: (adj as any).id,
          action: "adjustment_submitted",
          old_status: "draft",
          new_status: "pending_approval",
          changed_by: (await supabase.auth.getUser()).data.user?.id,
        } as any);
      }

      toast.success(submitForApproval ? "Adjustment submitted for approval" : "Adjustment saved as draft");
      setModalOpen(false);
      onMutate();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const handleTransition = async (id: string, newStatus: string, reason?: string) => {
    try {
      await transitionAdj.mutateAsync({ id, newStatus, reason });
      toast.success(`Adjustment ${newStatus.replace(/_/g, " ")}`);
      onMutate();
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Adjustments</h3>
        <Button size="sm" onClick={openModal}>
          <Plus className="mr-1 h-4 w-4" /> Add Adjustment
        </Button>
      </div>

      {!(adjustments ?? []).length ? (
        <p className="text-sm text-muted-foreground text-center py-4">No adjustments yet</p>
      ) : (
        <>
          <div className="border rounded-md">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Dir</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Responsible</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {(adjustments ?? []).map((a) => {
                  const vendor = vendors?.find(v => v.id === a.responsible_party_id);
                  return (
                    <TableRow key={a.id}>
                      <TableCell>
                        <Badge className={cn("text-[10px] capitalize", TYPE_COLORS[a.adjustment_type] ?? "")}>
                          {a.adjustment_type.replace(/_/g, " ")}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {a.direction === "debit"
                          ? <span className="flex items-center gap-0.5 text-destructive text-xs"><ArrowDown className="h-3 w-3" />Debit</span>
                          : <span className="flex items-center gap-0.5 text-green-600 text-xs"><ArrowUp className="h-3 w-3" />Credit</span>
                        }
                      </TableCell>
                      <TableCell className={cn("text-right font-mono font-medium text-sm", a.direction === "debit" ? "text-destructive" : "text-green-600")}>
                        {a.direction === "debit" ? "-" : "+"}{fmt(Number(a.amount))}
                      </TableCell>
                      <TableCell className="text-sm max-w-[200px] truncate">{a.description}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{vendor?.name ?? "—"}</TableCell>
                      <TableCell>
                        <Badge className={cn("text-[10px] capitalize", STATUS_COLORS[a.status] ?? "")}>
                          {a.status.replace(/_/g, " ")}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {a.status === "draft" && (
                              <>
                                <DropdownMenuItem onClick={() => handleTransition(a.id, "pending_approval")}>
                                  Submit for Approval
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleTransition(a.id, "voided")} className="text-destructive">
                                  Delete
                                </DropdownMenuItem>
                              </>
                            )}
                            {a.status === "pending_approval" && (
                              <>
                                <DropdownMenuItem onClick={() => handleTransition(a.id, "approved")}>
                                  Approve ✓
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => { setDisputeModal(a.id); setDisputeReason(""); }}>
                                  Dispute
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleTransition(a.id, "voided")} className="text-destructive">
                                  Void
                                </DropdownMenuItem>
                              </>
                            )}
                            {a.status === "approved" && (
                              <>
                                <DropdownMenuItem onClick={() => handleTransition(a.id, "applied")}>
                                  Apply to Invoice
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleTransition(a.id, "voided")} className="text-destructive">
                                  Void
                                </DropdownMenuItem>
                              </>
                            )}
                            {a.status === "disputed" && (
                              <>
                                <DropdownMenuItem onClick={() => handleTransition(a.id, "approved")}>
                                  Resolve → Approve
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleTransition(a.id, "voided")}>
                                  Resolve → Void
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {/* Totals */}
          <div className="flex items-center justify-between text-sm bg-muted/50 rounded-md p-3 font-mono">
            <span>Net adjustments:</span>
            <span className={cn("font-semibold", totals.net >= 0 ? "text-green-600" : "text-destructive")}>
              {totals.net >= 0 ? "+" : ""}{fmt(totals.net)}
            </span>
          </div>
          {(totals.pendingCount > 0 || totals.disputedCount > 0) && (
            <p className="text-xs text-muted-foreground">
              {totals.pendingCount > 0 && `${totals.pendingCount} pending approval`}
              {totals.pendingCount > 0 && totals.disputedCount > 0 && " · "}
              {totals.disputedCount > 0 && `${totals.disputedCount} disputed`}
            </p>
          )}
        </>
      )}

      {/* Add Adjustment Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Adjustment</DialogTitle>
            <DialogDescription>Create a backcharge, credit, or other adjustment</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Type</Label>
              <Select value={adjType} onValueChange={handleTypeChange}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ADJUSTMENT_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Direction</Label>
              <div className="flex gap-4 mt-1">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="radio" checked={direction === "debit"} onChange={() => setDirection("debit")} className="accent-destructive" />
                  <span className="text-destructive font-medium">● Debit</span>
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="radio" checked={direction === "credit"} onChange={() => setDirection("credit")} className="accent-green-600" />
                  <span className="text-green-600 font-medium">● Credit</span>
                </label>
              </div>
            </div>

            <div>
              <Label>Amount</Label>
              <Input type="number" step="0.01" min="0" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00" />
            </div>

            <div>
              <Label>Description *</Label>
              <Textarea value={description} onChange={e => setDescription(e.target.value)} rows={2} placeholder="Describe the adjustment..." />
            </div>

            <div>
              <Label>SOV Line</Label>
              <Select value={sovLineId || "__none__"} onValueChange={(v) => setSovLineId(v === "__none__" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="Optional — select SOV line" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {(sovLines ?? []).map((l: any) => (
                    <SelectItem key={l.id} value={l.id}>#{l.line_number} — {l.description}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Responsible Party</Label>
              <VendorCombobox
                value={responsiblePartyId}
                onChange={setResponsiblePartyId}
                vendors={(vendors ?? []) as any}
                placeholder="Select vendor..."
              />
            </div>

            {["backcharge", "damage", "rework"].includes(adjType) && (
              <div>
                <Label>Caused By</Label>
                <VendorCombobox
                  value={causedById}
                  onChange={setCausedById}
                  vendors={(vendors ?? []) as any}
                  placeholder="Who caused the issue?"
                />
              </div>
            )}


            {adjType === "backcharge" && (
              <div>
                <Label>Dispute Deadline</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !disputeDeadline && "text-muted-foreground")}>
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {disputeDeadline ? format(disputeDeadline, "PPP") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={disputeDeadline} onSelect={setDisputeDeadline} initialFocus className="p-3 pointer-events-auto" />
                  </PopoverContent>
                </Popover>
              </div>
            )}

            <div>
              <Label>Notes</Label>
              <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} placeholder="Optional notes..." />
            </div>
          </div>

          <DialogFooter className="flex gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button variant="secondary" disabled={busy} onClick={() => handleCreate(false)}>Save as Draft</Button>
            <Button disabled={busy} onClick={() => handleCreate(true)}>Submit for Approval →</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dispute Modal */}
      <Dialog open={!!disputeModal} onOpenChange={(o) => !o && setDisputeModal(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Dispute Adjustment</DialogTitle>
          </DialogHeader>
          <div>
            <Label>Reason</Label>
            <Textarea value={disputeReason} onChange={e => setDisputeReason(e.target.value)} rows={3} placeholder="Why is this being disputed?" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDisputeModal(null)}>Cancel</Button>
            <Button disabled={!disputeReason.trim()} onClick={() => {
              if (disputeModal) handleTransition(disputeModal, "disputed", disputeReason);
              setDisputeModal(null);
            }}>Submit Dispute</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
